import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Spinner as ChakraSpinner,
  useDisclosure,
  PopoverFooter,
} from '@chakra-ui/react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PDFExport } from '@progress/kendo-react-pdf';

import DownloadIcon from 'components/Icons/DownloadIcon';
import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';
import bloxBiService from 'services/bloxBiService';
import {
  getSelectedDashboardScenario,
  getTimeFilter,
  getDashboard,
  getCompareDashboardScenario,
  getReportingAnchorPeriod,
  getReportingWindow,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { resolveReportingPeriod } from 'utils/reportingPeriodUtils';

import Preview from './Preview';
import { PageInstruments } from './Instruments/proptypes';
import './pdf-styles.css'; // You'll need to create this file

interface PreviewModalProperties {
  isOpen: boolean;
  onClose: () => void;
  selectedPages: any[];
}

const PreviewModal = ({ isOpen, onClose, selectedPages }: PreviewModalProperties) => {
  const dashboard = useSelector(getDashboard);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const timeFilter = useSelector(getTimeFilter);
  const targetReference = useRef<any>();
  const [pdfExport, setPDFExport] = useState(false);
  const [allPageInstruments, setAllPageInstruments] = useState<PageInstruments>({});
  const [allInstrumentsData, setAllInstrumentsData] = useState<PageInstruments>({});
  const [isPageDataLoading, setIsPageDataLoading] = useState(false);
  const [isPageInstrumentsLoading, setIsPageInstrumentsLoading] = useState(false);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [pageSearch, setPageSearch] = useState('');
  const {
    isOpen: isExportPopoverOpen,
    onOpen: onExportPopoverOpen,
    onClose: onExportPopoverClose,
  } = useDisclosure();

  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const dashboardTimeFilter = useSelector(getTimeFilter);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const anchorPeriod = useSelector(getReportingAnchorPeriod);
  const reportingWindow = useSelector(getReportingWindow);
  const model = useSelector(getBlocks);
  const fyStartMonth: string =
    (model?.time_properties?.fy_start_month as string | undefined) ?? 'Jan';
  const contentLoading = isPageDataLoading || isPageInstrumentsLoading || pdfExport;
  const previewContentLoading = isPageDataLoading || isPageInstrumentsLoading;
  const pages = dashboard?.pages || [];

  const filteredPages = useMemo(
    () =>
      pages
        .slice()
        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
        .filter((page: any) => page.name?.toLowerCase().includes(pageSearch.trim().toLowerCase())),
    [pages, pageSearch],
  );

  const selectedPagesForPreview = useMemo(
    () =>
      pages
        .slice()
        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
        .filter((page: any) => selectedPageIds.includes(page.id)),
    [pages, selectedPageIds],
  );

  const hasSelectedPages = selectedPagesForPreview.length > 0;
  const allVisibleSelected =
    filteredPages.length > 0 &&
    filteredPages.every((page: any) => selectedPageIds.includes(page.id));
  const someVisibleSelected =
    filteredPages.some((page: any) => selectedPageIds.includes(page.id)) && !allVisibleSelected;
  useEffect(() => {
    if (isOpen) {
      setAllPageInstruments({});
      setAllInstrumentsData({});
      setIsPageDataLoading(false);
      setIsPageInstrumentsLoading(false);
      const initialSelectedIds =
        selectedPages?.map((page: any) => page.id).filter((id: string) => !!id) || [];

      if (initialSelectedIds.length > 0) {
        setSelectedPageIds(initialSelectedIds);
      } else if (pages?.length) {
        setSelectedPageIds(pages?.map((page: any) => page.id).filter((id: string) => !!id));
      } else {
        setSelectedPageIds([]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (!pages?.length || !selectedDashboardScenario || !isOpen || !anchorPeriod) {
        setIsPageDataLoading(false);
        setIsPageInstrumentsLoading(false);
        return;
      }

      // Reset data first, then start loading
      setAllPageInstruments({});
      setAllInstrumentsData({});
      setIsPageDataLoading(true);
      setIsPageInstrumentsLoading(true);

      try {
        const compareScenarioId = compareDashboardScenario?.id || undefined;

        // Resolve the anchor + window to concrete start/end for the export call.
        // Pass fyStartMonth so YTD/FY calculations respect the model's fiscal year.
        const { start: reportingStart, end: reportingEnd } = resolveReportingPeriod(
          anchorPeriod,
          reportingWindow,
          fyStartMonth,
        );

        const response = await bloxBiService.dashboard_full_export_pdf(
          dashboard?.id,
          dashboardTimeFilter?.start ?? '',
          dashboardTimeFilter?.end ?? '',
          selectedDashboardScenario?.id,
          dashboard?.model_id,
          anchorPeriod,
          reportingWindow,
          reportingStart,
          reportingEnd,
          compareScenarioId,
        );
        const instrumentDataMap: any = {};
        const instrumentsMap: any = {};
        response.data.pages?.forEach((page: any) => {
          instrumentsMap[page.page_id] = page.metadata.instruments;
          instrumentDataMap[page.page_id] = page.data_values;
        });

        setAllPageInstruments(instrumentsMap);
        setIsPageDataLoading(false);

        setAllInstrumentsData(instrumentDataMap);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
        // Reset to empty state on error
        setAllPageInstruments({});
        setAllInstrumentsData({});
      } finally {
        setIsPageDataLoading(false);
        setIsPageInstrumentsLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [
    selectedDashboardScenario,
    dashboardTimeFilter,
    anchorPeriod,
    reportingWindow,
    compareDashboardScenario,
    isOpen,
  ]);

  if (!dashboard || !selectedScenario || !timeFilter) {
    return null;
  }

  const onExport = () => {
    onExportPopoverClose();
    setPDFExport(true);

    try {
      if (targetReference.current) {
        setTimeout(() => {
          targetReference.current.save();
          setPDFExport(false);
          onClose();
        }, 1000);
      } else {
        setTimeout(() => {
          setPDFExport(false);
          onClose();
        }, 1000);
      }
    } catch {
      setTimeout(() => {
        setPDFExport(false);
        onClose();
      }, 1000);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent
        minW={'calc(210mm + 100px)'}
        maxW={'90vw'}
        borderRadius='14px'
        overflow='hidden'
        border='1px solid #D9D9D9'
      >
        <ModalHeader bg='white' borderBottom='1px solid #E8E9EB' p='16px'>
          <Flex width='100%' justifyContent='flex-end' alignItems='center' gap='11px'>
            <Popover
              placement='left-start'
              isOpen={isExportPopoverOpen}
              onOpen={onExportPopoverOpen}
              onClose={onExportPopoverClose}
              offset={[0, 12]}
            >
              <PopoverTrigger>
                <Button
                  h='32px'
                  px='14px'
                  bg='#8B5CF6'
                  borderRadius='9px'
                  color='white'
                  fontFamily='Roboto Regular'
                  fontWeight='500'
                  fontSize='13px'
                  width='fit-content'
                  _hover={{ bg: '#7C4DEF' }}
                  leftIcon={
                    contentLoading ? (
                      <ChakraSpinner size='sm' thickness='3px' />
                    ) : (
                      <FigmaFileDownloadIcon width={14} height={14} color='white' />
                    )
                  }
                  isLoading={contentLoading}
                  disabled={!(pages?.length && pages?.length > 0)}
                  loadingText='Content is being prepared'
                >
                  Export ({selectedPageIds.length}/
                  {pages?.length && pages?.length > 0 ? pages?.length : 0} pages)
                </Button>
              </PopoverTrigger>
              <PopoverContent
                maxW='300px'
                bg='white'
                border='1px solid #D9D9D9'
                p='14px'
                borderRadius='14px'
                boxShadow='0px 8px 24px rgba(18, 24, 38, 0.16)'
              >
                <PopoverBody p={0}>
                  <Text
                    fontFamily='Comfortaa'
                    fontWeight='bold'
                    fontSize='14px'
                    color='#121826'
                    mb='11px'
                  >
                    Export Pages
                  </Text>
                  <Input
                    placeholder='Search pages'
                    mb='9px'
                    bg='white'
                    border='1px solid #D9D9D9'
                    borderRadius='9px'
                    h='32px'
                    fontFamily='Roboto Regular'
                    fontSize='13px'
                    color='#121826'
                    _placeholder={{ color: '#BEBEBE' }}
                    _focus={{ borderColor: '#8B5CF6', boxShadow: 'none' }}
                    value={pageSearch}
                    onChange={(event) => setPageSearch(event.target.value)}
                  />
                  <Flex
                    align='center'
                    gap='7px'
                    mb='9px'
                    cursor='pointer'
                    onClick={() => {
                      if (allVisibleSelected) {
                        setSelectedPageIds([]);
                      } else {
                        const idsToAdd = pages
                          .slice()
                          .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                          .map((page: any) => page.id)
                          .filter((id: string) => !!id);
                        setSelectedPageIds(idsToAdd);
                      }
                    }}
                  >
                    <Checkbox
                      size='sm'
                      colorScheme='purple'
                      isChecked={allVisibleSelected}
                      isIndeterminate={someVisibleSelected}
                      pointerEvents='none'
                    />
                    <Text fontFamily='Roboto Bold' fontWeight='700' fontSize='13px' color='#121826'>
                      Select all
                    </Text>
                  </Flex>
                  <Box
                    maxH='200px'
                    overflowY='auto'
                    className='scroll'
                    display='flex'
                    flexDirection='column'
                    gap='7px'
                  >
                    {filteredPages.map((page: any) => (
                      <Flex
                        key={page.id}
                        align='center'
                        gap='7px'
                        cursor='pointer'
                        onClick={() => {
                          setSelectedPageIds((previous) =>
                            previous.includes(page.id)
                              ? previous.filter((id) => id !== page.id)
                              : [...previous, page.id],
                          );
                        }}
                      >
                        <Checkbox
                          size='sm'
                          colorScheme='purple'
                          isChecked={selectedPageIds.includes(page.id)}
                          pointerEvents='none'
                        />
                        <Text
                          fontFamily='Roboto Regular'
                          fontSize='13px'
                          color='#121826'
                          noOfLines={1}
                        >
                          {page.name}
                        </Text>
                      </Flex>
                    ))}
                    {filteredPages.length === 0 && (
                      <Text fontFamily='Roboto Regular' fontSize='13px' color='#708090'>
                        No pages found.
                      </Text>
                    )}
                  </Box>
                </PopoverBody>
                <PopoverFooter
                  display='flex'
                  justifyContent='flex-end'
                  border='none'
                  p={0}
                  mt='11px'
                  gap='9px'
                >
                  <Button
                    h='30px'
                    px='12px'
                    bg='white'
                    border='1px solid #D9D9D9'
                    borderRadius='8px'
                    fontFamily='Roboto Regular'
                    fontWeight='500'
                    fontSize='12px'
                    color='#121826'
                    _hover={{ bg: 'gray.50' }}
                    onClick={onExportPopoverClose}
                  >
                    Cancel
                  </Button>

                  <Button
                    h='30px'
                    px='12px'
                    bg='#8B5CF6'
                    borderRadius='8px'
                    color='white'
                    fontFamily='Roboto Regular'
                    fontWeight='500'
                    fontSize='12px'
                    _hover={{ bg: '#7C4DEF' }}
                    leftIcon={<DownloadIcon />}
                    disabled={!hasSelectedPages}
                    onClick={() => {
                      setTimeout(() => onExport(), 300);
                    }}
                  >
                    Export
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
            <Button
              h='32px'
              px='14px'
              bg='white'
              border='1px solid #D9D9D9'
              borderRadius='9px'
              fontFamily='Roboto Regular'
              fontWeight='500'
              fontSize='13px'
              color='#121826'
              width='fit-content'
              _hover={{ bg: 'gray.50' }}
              onClick={onClose}
              leftIcon={contentLoading ? <ChakraSpinner size='sm' thickness='3px' /> : <></>}
              isLoading={contentLoading}
              disabled={contentLoading}
            >
              Cancel
            </Button>
          </Flex>
        </ModalHeader>
        <ModalBody
          className='pdf-preview-container scroll'
          maxH='75vh'
          overflowY='auto'
          overflowX='auto'
        >
          <Box>
            <PDFExport
              paperSize='auto' // not a4 managed from css,as content was cropping
              margin='0.1cm'
              ref={targetReference}
              fileName={`${dashboard?.name} .pdf`}
              author='BLOX'
              title={`${dashboard?.name} Dashboard Export`}
              subject='Dashboard Export'
              creator='BLOX'
              date={new Date()}
              keepTogether='page'
              landscape={false}
              scale={0.4}
              forcePageBreak='.page-break'
            >
              <Box className='a4-preview-wrapper'>
                {previewContentLoading && (
                  <>
                    <Text
                      color='black'
                      height={'80vh'}
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                    >
                      Loading preview...
                    </Text>
                  </>
                )}
                {!previewContentLoading && hasSelectedPages && (
                  <Preview
                    selectedPages={selectedPagesForPreview}
                    allPageInstruments={allPageInstruments}
                    allInstrumentsData={allInstrumentsData}
                    isPageDataLoading={isPageDataLoading}
                    isPageInstrumentsLoading={isPageInstrumentsLoading}
                  />
                )}
                {!previewContentLoading && !hasSelectedPages && (
                  <Text
                    color='black'
                    height={'80vh'}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    Please select at least one page to export.
                  </Text>
                )}
              </Box>
            </PDFExport>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PreviewModal;
